import { createApi } from '@reduxjs/toolkit/query/react'
import { CreateTagType, Tag } from '../types/models/tag'
import baseQueryWithReauth from './baseQuery'
import { Filter } from '../types/common'
import { apiTags } from './apiTags'

export const tagApi = createApi({
  reducerPath: 'tagApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.Tag, apiTags.TagProperties],

  endpoints: (builder) => ({
    getTags: builder.query<
      { tags: Tag[]; total: number },
      {
        project_id: string
        dataset_id: string
        limit?: number
        page?: number
        sort_key?: string
        sort_val?: number
      }
    >({
      query: (params) => ({
        url: 'tags',
        params,
      }),
      providesTags: (result) => [
        { type: apiTags.Tag, id: 'LIST' },
        ...(result?.tags?.map(({ id }) => ({ type: apiTags.Tag, id })) || []),
      ],
      // keepUnusedDataFor: 300, // 5 minutes
    }),

    queryTags: builder.query<
      { tags: Tag[]; total: number },
      {
        page: number
        limit: number
        sort_key?: string // 'name' | 'created_at' | 'updated_at'
        sort_val?: number // 1 for ascending, -1 for descending
        operator?: 'or' | 'and'
        filters: Filter[] // Array of filters
      }
    >({
      query: (params) => ({
        url: 'tags/query',
        method: 'POST',
        body: params,
      }),
      providesTags: (result) => [
        { type: apiTags.Tag, id: 'LIST' },
        ...(result?.tags?.map(({ id }) => ({
          type: apiTags.Tag,
          id,
        })) || []),
      ],
      // keepUnusedDataFor: 300, // 5 minutes
    }),

    getTagById: builder.query<Tag, string>({
      query: (id) => `tags/${id}`,
      providesTags: (result, error, id) => [{ type: apiTags.Tag, id }],
      // keepUnusedDataFor: 300, // 5 minutes
    }),

    createTag: builder.mutation<Tag, CreateTagType>({
      query: (tagData) => ({
        url: 'tags',
        method: 'POST',
        body: tagData,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: apiTags.Tag, id: 'LIST' },
        { type: apiTags.Tag, id: `${result?.id}` },
        { type: apiTags.TagProperties, id: arg.dataset_id },
        { type: apiTags.AnnotationStats, id: arg.dataset_id },
        { type: apiTags.AnnotationStats, id: 'LIST' },
        { type: apiTags.Annotation, id: 'LIST' },
        { type: apiTags.Content, id: 'LIST' },
        { type: apiTags.Metadata, id: arg.project_id },
        { type: apiTags.Metadata, id: 'LIST' },
      ],
    }),

    updateTag: builder.mutation<
      Tag,
      {
        id: string
        dataset_id: string
        tagData: {
          name?: string
          property?: string[]
          color?: number
        }
      }
    >({
      query: ({ id, tagData }) => ({
        url: `tags/${id}`,
        method: 'PATCH',
        body: tagData,
      }),
      invalidatesTags: (result, error, { id, dataset_id }) => [
        { type: apiTags.Tag, id },
        { type: apiTags.TagProperties, id: dataset_id },
      ],
    }),

    deleteTag: builder.mutation<void, { tagId: string; projectId: string; datasetId: string }>({
      query: ({ tagId }) => ({
        url: `tags/${tagId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          await new Promise((resolve) => setTimeout(resolve, 500))
          dispatch({
            type: apiTags.Tag,
            id: 'LIST',
          })
          dispatch({
            type: apiTags.Tag,
            id: arg.tagId,
          })
          dispatch({
            type: apiTags.TagProperties,
            id: arg.datasetId,
          })
          dispatch({
            type: apiTags.Annotation,
            id: 'LIST',
          })
          dispatch({
            type: apiTags.AnnotationStats,
            id: arg.datasetId,
          })
          dispatch({
            type: apiTags.AnnotationStats,
            id: 'LIST',
          })
          dispatch({ type: apiTags.Annotation, id: 'LIST' })
          dispatch({ type: apiTags.Dataset, id: arg.datasetId })
        } catch (error) {
          console.error('Failed to delete tag:', error)
        }
      },
    }),

    getTagProperties: builder.query<string[], string>({
      query: (dataset_id) => ({
        url: 'tags/properties',
        params: { dataset_id },
      }),
      providesTags: (result, error, dataset_id) => [
        { type: apiTags.TagProperties, id: dataset_id },
      ],
      // keepUnusedDataFor: 600, // 10 minutes since properties change very rarely
    }),
  }),
})

export const {
  useGetTagsQuery,
  useGetTagByIdQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useGetTagPropertiesQuery,
  useQueryTagsQuery,
} = tagApi

export const useSelectTagById = (tagId: string) => {
  const { data } = useGetTagByIdQuery(tagId)
  return data
}

export const {
  util: { invalidateTags },
} = tagApi
